import React, { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { AuthorBlock } from '../AuthorBlock';
import { ISocialPostComment } from 'types/src/types';
import { Button } from '@workspace/ui/components/button';
import { Icon, Spinner } from '@/src/components/ui';
import { CommentActions } from './CommentActions';
import { useSocialPostReplies } from '@/src/hooks/use-fetch-posts-comments';
import { useBoolean } from '@/src/hooks/use-boolean';
import { cn } from '@workspace/ui/lib/utils';
import { useCommentScroll } from './CommentScrollContext';
import PageLoader from '@/src/components/ui/PageLoader';
import { superlikeComment } from '@/src/actions/social-comments';
import { useToast } from '@/src/components/ui';
import { useQueryClient } from '@tanstack/react-query';
import { DotsButton } from '../DotsButton';

type Props = {
  comment: ISocialPostComment;
  isMyComment: boolean;
  isAbleToSuperLike: boolean;
  isReply: boolean;
  isReplyModal: boolean;
  isLastReply?: boolean;
  isMyPost: boolean;
  isMobile?: boolean;
  postAvatarUrl?: string;
  postAuthorId?: number;
};

export const SocialCommentItem = ({
  comment,
  isMyComment,
  isAbleToSuperLike,
  isReply = false,
  isReplyModal = false,
  isLastReply = false,
  isMyPost,
  isMobile,
  postAvatarUrl,
  postAuthorId,
}: Props) => {
  const {
    author,
    content,
    createdAt,
    postId,
    taggedUserUniqueLink,
    deletedAt,
    isSuperLiked,
  } = comment;
  const isShowReplies = useBoolean(false);
  const toast = useToast();
  const qc = useQueryClient();

  const { register, scrollTo, highlightedId, openThreadId, clearOpenThread } =
    useCommentScroll();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useSocialPostReplies(postId, comment.id, isShowReplies.value);

  const replies = data?.pages?.flatMap((p) => p.data?.comments ?? []) ?? [];
  const isHighlighted = highlightedId === comment.id;

  const htmlWithMention = useMemo(() => {
    return injectMentionIntoHtml(content, {
      text: taggedUserUniqueLink ?? '',
      replyToId: comment.replyToCommentId ?? null,
    });
  }, [content, taggedUserUniqueLink, comment.replyToCommentId]);

  const onContentClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    const target = e.target as HTMLElement | null;
    const el =
      (target?.closest?.('[data-mention="1"]') as HTMLElement | null) ||
      (target?.closest?.(
        'span.mention[data-type="mention"]',
      ) as HTMLElement | null);

    if (!el) return;

    const replyToRaw = el.getAttribute('data-replyto');
    const replyToId = replyToRaw ? Number(replyToRaw) : NaN;
    if (Number.isFinite(replyToId)) {
      scrollTo(replyToId, { highlight: true, highlightMs: 1000 });
    }
  };

  useEffect(() => {
    if (!isReply && openThreadId === comment.id && !isShowReplies.value) {
      isShowReplies.onTrue();
    }
  }, [openThreadId, comment.id, isReply, isShowReplies, clearOpenThread]);

  useEffect(() => {
    if (!isReply && openThreadId === comment.id && isShowReplies.value) {
      clearOpenThread();
    }
  }, [openThreadId, comment.id, isReply, isShowReplies.value, clearOpenThread]);

  const queryKey = isReply
    ? (['postComments', postId, 'replies', comment.parentId!] as const)
    : (['postComments', postId, 'root'] as const);

  const handleSuperlike = async () => {
    try {
      await superlikeComment(comment.id);

      qc.invalidateQueries({ queryKey: ['profile'] });

      qc.invalidateQueries({ queryKey });
      toast.success('Comment superliked!');
    } catch (error) {
      toast.error('Failed to superlike comment.');
      console.error('Error superliking comment:', error);
    }
  };

  return (
    <div
      ref={(el) => register(comment.id, el)}
      className={cn('md:mt-7', { 'md:mt-3': isReply })}
    >
      {isLoading && <PageLoader />}

      <div
        className={cn(
          'w-full flex md:gap-4 gap-2.5 md:mx-3 px-3 mt-2 md:mt-0',
          {
            'md:pl-0 pl-2.5 pr-10': isReplyModal,
          },
        )}
      >
        <div className={cn('flex flex-col', {})}>
          <a
            href={`/${author.uniqueLink}`}
            className="size-[42px] rounded-full hover:border-blue-primary box-content border-1 border-transparent transition-colors duration-400"
          >
            <Image
              src={author.avatarUrl || '/images/default-avatar.png'}
              alt={author.name || 'User Avatar'}
              width={42}
              height={42}
            />
          </a>
          {isReply && !isLastReply && (
            <hr
              className={cn(
                'w-0.5 border-l border-gray-300 ml-5 h-full py-10 md:mt-3',
              )}
            />
          )}
          {isReplyModal && (
            <hr
              className={cn('w-0.5 border-l border-gray-300 ml-5 h-full py-6')}
            />
          )}
          {isShowReplies.value && !isReply && (
            <div className="relative h-full py-10 md:mt-3">
              <div className="absolute left-1/2 top-0 h-full w-px bg-gray-300 -translate-x-1/2" />
              <button
                onClick={isShowReplies.onFalse}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 size-7.5 flex items-center justify-center rounded-full bg-[#EDEDEF] hover:bg-blue-primary transition-all duration-300 cursor-pointer z-10 hover:rotate-90"
              >
                <Icon name="splash" className="size-4" />
              </button>
            </div>
          )}
        </div>
        <div className="w-full flex flex-col max-w-[570px]">
          <div className="flex justify-between items-start w-full">
            <AuthorBlock
              name={author.fullName || ''}
              uniqueLink={author.uniqueLink || ''}
              level={author.level || 0}
              createdAt={createdAt as Date}
            />
            {!isMyComment && !isSuperLiked && isMyPost && (
              <div className="group relative md:block hidden">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!isAbleToSuperLike}
                  className={cn('w-fit p-1 md:text-xs', {})}
                  onClick={handleSuperlike}
                >
                  <Icon name="empty-heart" className="" />
                  Super Like
                  {!isAbleToSuperLike && (
                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap px-2 py-1 rounded-md bg-white border text-[10px] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 ">
                      <span className="font-bold">
                        Superlike unlocks at lvl 2
                      </span>
                    </div>
                  )}
                </Button>
              </div>
            )}
            {isSuperLiked && (
              <Button
                variant="ghost"
                size="sm"
                className="w-fit p-1 md:text-xs disabled:opacity-100 disabled:cursor-not-allowed disabled:border disabled:border-blue-primary md:flex hidden"
                disabled
              >
                <Icon name="logo-heart" className="" />
                Super Liked
              </Button>
            )}
            {isMobile && (
              <DotsButton postComment={comment} isMyComment={isMyComment} />
            )}
          </div>
          {deletedAt ? (
            <div
              className={cn(
                'text-gray-500 italic text-sm md:text-base mt-2',
                isHighlighted &&
                  'bg-pink-accent/5 transition-colors duration-700 ease-out rounded-lg',
              )}
            >
              [deleted comment]
            </div>
          ) : (
            <div
              className={cn(
                'md:mt-7 transition-colors duration-300 rounded-lg whitespace-pre-line mt-4 text-sm md:text-base w-fit',
                { 'md:mt-3': isReply || isReplyModal },
                isHighlighted &&
                  'bg-pink-accent/5 transition-colors duration-700 ease-out rounded-lg',
              )}
              id={`comment-${comment.id}`}
              style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
              onClick={onContentClick}
              dangerouslySetInnerHTML={{ __html: htmlWithMention }}
            />
          )}

          {!isReplyModal && (
            <CommentActions
              comment={comment}
              isMyComment={isMyComment}
              isShowReplies={isShowReplies.onTrue}
              queryKey={queryKey}
              isMobile={isMobile}
              postAuthorAvatarUrl={postAvatarUrl}
              isSuperliked={isSuperLiked}
              isMyPost={isMyPost}
              isAbleToSuperLike={isAbleToSuperLike}
            />
          )}
        </div>
      </div>
      <div
        className={cn(
          'overflow-hidden transition-all duration-300 ease-out',
          isShowReplies.value
            ? 'opacity-100 translate-y-0 max-h-[70vh] overflow-auto no-scrollbar border-b-1 pb-4'
            : 'opacity-0 -translate-y-1 max-h-0 pointer-events-none',
        )}
      >
        {replies.map((r) => (
          <SocialCommentItem
            key={r.id}
            comment={r}
            isMyComment={postAuthorId === r.author.id}
            isMyPost={isMyPost}
            isAbleToSuperLike={isAbleToSuperLike}
            isReply
            isReplyModal={false}
            isLastReply={r.id === replies[replies.length - 1].id}
          />
        ))}

        {hasNextPage && (
          <Button
            variant="link"
            disabled={isLoading || isFetchingNextPage}
            onClick={() => fetchNextPage()}
            className="font-semibold underline text-center w-full my-4 block mx-auto hover:text-blue-primary"
          >
            Show more
          </Button>
        )}
      </div>
    </div>
  );
};

function hasLeadingMention(html: string) {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const p = doc.body.firstElementChild;
    const container = p?.tagName?.toLowerCase() === 'p' ? p : doc.body;

    const firstEl = container.firstElementChild as HTMLElement | null;
    if (!firstEl) return false;

    if (
      firstEl.classList.contains('mention') &&
      firstEl.dataset.type === 'mention'
    ) {
      return true;
    }

    if (firstEl.dataset.mention === '1') {
      return true;
    }

    return false;
  } catch {
    return false;
  }
}

function injectMentionIntoHtml(
  html: string,
  mention?: { text: string; replyToId?: number | null },
) {
  if (!mention?.text) return html;

  if (hasLeadingMention(html)) return html;

  const replyTo = mention.replyToId ?? '';
  const mentionHtml = `<span data-mention="1" data-replyto="${replyTo}" class="mention">@${mention.text}</span>&nbsp;`;

  const pOpen = html.match(/^<p(\s[^>]*)?>/i);
  if (pOpen) return html.replace(pOpen[0], `${pOpen[0]}${mentionHtml}`);

  return `${mentionHtml}${html}`;
}
